import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';
import type {
  AddToCartData,
  ConversionEventData,
  LeadData,
  PurchaseData,
  RemoveFromCartData,
  ViewContentData,
} from '@/events/conversion';

import type {
  ConversionEventOrigin,
  ConversionEventType,
  TrackConversionOptions,
} from '../contracts';
import {
  CommerceResolver,
  type CommerceEnrichmentEvent,
  type CommerceEventType,
} from '@/services/commerce';
import type { CommerceState, ProductCatalog } from '@/services/commerce';
import { OrderCalculator } from '@/services/commerce';
import { buildCanonicalDedupeKey } from '../dedupe/dedupe-key';
import type { ConversionTrackFn } from './BaseTracker';
import { AddPaymentInfoTracker } from './AddPaymentInfoTracker';
import { AddToCartTracker } from './AddToCartTracker';
import { InitiateCheckoutTracker } from './InitiateCheckoutTracker';
import { LeadTracker } from './LeadTracker';
import { PurchaseTracker } from './PurchaseTracker';
import { RemoveFromCartTracker } from './RemoveFromCartTracker';
import { SearchTracker } from './SearchTracker';
import { ViewContentTracker } from './ViewContentTracker';

const EVENT_TO_ENRICHMENT: Partial<
  Record<ConversionEventType, CommerceEnrichmentEvent>
> = {
  VIEW_CONTENT: 'viewContent',
  ADD_TO_CART: 'addToCart',
  REMOVE_FROM_CART: 'removeFromCart',
  INITIATE_CHECKOUT: 'initiateCheckout',
  ADD_PAYMENT_INFO: 'addPaymentInfo',
  PURCHASE: 'purchase',
  LEAD: 'lead',
};

const COMMERCE_EVENT_TYPES: readonly CommerceEventType[] = [
  'ADD_TO_CART',
  'REMOVE_FROM_CART',
  'INITIATE_CHECKOUT',
  'PURCHASE',
  'VIEW_CONTENT',
  'ADD_PAYMENT_INFO',
];

export type TrackerFactoryCommerceDeps = {
  readonly catalog: ProductCatalog;
  readonly state: CommerceState;
};

export class TrackerFactory {
  private readonly context: PixelRuntimeContext;

  private readonly trackFn: ConversionTrackFn;

  private readonly dedupeSessionId: string;

  private readonly resolver: CommerceResolver | null;

  private readonly commerceState: CommerceState | null;

  readonly purchase: PurchaseTracker;

  readonly lead: LeadTracker;

  readonly addToCart: AddToCartTracker;

  readonly removeFromCart: RemoveFromCartTracker;

  readonly initiateCheckout: InitiateCheckoutTracker;

  readonly addPaymentInfo: AddPaymentInfoTracker;

  readonly viewContent: ViewContentTracker;

  readonly search: SearchTracker;

  constructor(
    context: PixelRuntimeContext,
    trackFn: ConversionTrackFn,
    dedupeSessionId: string,
    commerce?: TrackerFactoryCommerceDeps
  ) {
    this.context = context;
    this.trackFn = trackFn;
    this.dedupeSessionId = dedupeSessionId;
    this.commerceState = commerce?.state ?? null;

    if (commerce) {
      const cfg = context.services.commerce.config;
      this.resolver = new CommerceResolver({
        catalog: commerce.catalog,
        state: commerce.state,
        calculator: new OrderCalculator(),
        defaultCurrency: cfg?.currency,
        calculateValueFrom: cfg?.cart?.calculateValueFrom ?? 'cart',
      });
    } else {
      this.resolver = null;
    }

    this.purchase = new PurchaseTracker(this);
    this.lead = new LeadTracker(this);
    this.addToCart = new AddToCartTracker(this);
    this.removeFromCart = new RemoveFromCartTracker(this);
    this.initiateCheckout = new InitiateCheckoutTracker(this);
    this.addPaymentInfo = new AddPaymentInfoTracker(this);
    this.viewContent = new ViewContentTracker(this);
    this.search = new SearchTracker(this);
  }

  trackAuto(
    type: ConversionEventType,
    data?: ConversionEventData,
    options?: TrackConversionOptions
  ): void {
    this.dispatch(type, data, options?.origin ?? 'auto:selector');
  }

  dispatchManual(type: ConversionEventType, data?: ConversionEventData): void {
    this.dispatch(type, data, 'manual');
  }

  normalize(
    type: ConversionEventType,
    data: ConversionEventData | undefined
  ): ConversionEventData {
    switch (type) {
      case 'SEARCH': {
        const search = data as { query?: string; searchTerm?: string };
        const query = search?.query ?? search?.searchTerm ?? '';
        if (!query.trim()) {
          throw new Error('[Conversion] SEARCH requires query');
        }
        return { query: query.trim() };
      }
      case 'PURCHASE': {
        const purchase = data as PurchaseData;
        if (purchase?.value != null && purchase.value < 0) {
          throw new Error('[Conversion] PURCHASE value cannot be negative');
        }
        return purchase ?? {};
      }
      case 'LEAD': {
        const lead = data as LeadData;
        if (!lead?.email && !lead?.phone && !lead?.name) {
          throw new Error('[Conversion] LEAD requires email, phone or name');
        }
        return lead;
      }
      case 'VIEW_CONTENT': {
        // productId é chave interna de enrichment (ex.: link → catalog);
        // o contrato público de ViewContentData usa contentId.
        const view = { ...((data ?? {}) as Record<string, unknown>) };
        delete view.productId;
        return view as ViewContentData;
      }
      default:
        return data ?? {};
    }
  }

  buildDedupeKey(
    type: ConversionEventType,
    data: ConversionEventData | undefined,
    origin: ConversionEventOrigin
  ): string {
    return buildCanonicalDedupeKey(type, data, origin, this.dedupeSessionId);
  }

  isManualEnabled(
    key:
      | 'purchase'
      | 'lead'
      | 'addToCart'
      | 'removeFromCart'
      | 'initiateCheckout'
      | 'addPaymentInfo'
      | 'viewContent'
      | 'search'
  ): boolean {
    return this.context.config.modules.conversion.manual[key].enabled;
  }

  private dispatch(
    type: ConversionEventType,
    data: ConversionEventData | undefined,
    origin: ConversionEventOrigin
  ): void {
    const enriched = this.enrichCommerce(type, data);
    const normalized = this.normalize(type, enriched);
    this.syncCommerceState(type, normalized);
    const key = buildCanonicalDedupeKey(
      type,
      normalized,
      origin,
      this.dedupeSessionId
    );
    this.trackFn(type, normalized, { dedupeKey: key, origin });
  }

  private enrichCommerce(
    type: ConversionEventType,
    data: ConversionEventData | undefined
  ): ConversionEventData | undefined {
    if (!this.resolver || !isCommerceEventType(type)) {
      return data;
    }

    const enrichmentKey = EVENT_TO_ENRICHMENT[type];
    if (!enrichmentKey) return data;

    const commerce = this.context.services.commerce;
    if (!commerce.isEnrichmentEnabled(enrichmentKey)) {
      return data;
    }

    const source = commerce.getEnrichmentSource(enrichmentKey);
    const result = this.resolver.resolve({
      eventType: type,
      payload: (data ?? {}) as Record<string, unknown>,
      enrichmentSource: source,
    });

    return result.data as ConversionEventData;
  }

  /**
   * Sync controlado do checkout/cart após resolução.
   * - ADD_TO_CART: upsertItem (sem incremento automático)
   * - REMOVE_FROM_CART: removeItem
   * - Demais eventos: no-op (só leitura via CommerceResolver)
   * Sem productId → não escreve.
   */
  private syncCommerceState(
    type: ConversionEventType,
    data: ConversionEventData
  ): void {
    if (!this.commerceState) return;

    if (type === 'ADD_TO_CART') {
      this.syncAddToCart(data as AddToCartData);
      return;
    }

    if (type === 'REMOVE_FROM_CART') {
      this.syncRemoveFromCart(data as RemoveFromCartData);
    }
  }

  private syncAddToCart(data: AddToCartData): void {
    if (!this.commerceState) return;

    const productId =
      typeof data.productId === 'string' ? data.productId.trim() : '';
    if (!productId) return;

    const quantity =
      typeof data.quantity === 'number' && Number.isFinite(data.quantity)
        ? data.quantity
        : 1;

    // quantity <= 0: CommerceState.upsertItem remove o item
    this.commerceState.upsertItem({
      productId,
      quantity,
      ...(typeof data.price === 'number' && Number.isFinite(data.price)
        ? { price: data.price }
        : {}),
      ...(typeof data.currency === 'string' && data.currency.trim()
        ? { currency: data.currency.trim() }
        : {}),
    });
  }

  private syncRemoveFromCart(data: RemoveFromCartData): void {
    if (!this.commerceState) return;

    const productId =
      typeof data.productId === 'string' ? data.productId.trim() : '';
    if (!productId) return;

    this.commerceState.removeItem(productId);
  }
}

function isCommerceEventType(
  type: ConversionEventType
): type is CommerceEventType {
  return (COMMERCE_EVENT_TYPES as readonly string[]).includes(type);
}
